import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { SeoHead } from "@/components/SeoHead";
import { AlertTriangle, Check, Loader2 } from "lucide-react";

type Details = {
  authorization_id: string;
  redirect_uri: string;
  client: { id: string; name: string; uri: string; logo_uri: string };
  user: { id: string; email: string };
  scope: string;
};

type Workspace = { workspace_id: string; label: string; account_tier: string; eligible: boolean };

const CAPABILITIES = [
  "Search and read your excerpts, My Library and Corpus",
  "Read and create Notes",
  "Read and create Composer Drafts",
];

const SCOPE_LABELS: Record<string, string> = {
  openid: "Confirm who you are",
  email: "Share your email address",
  profile: "Share your basic profile",
  offline_access: "Stay connected until you disconnect",
  phone: "Share your phone number",
};

function hostOf(uri: string): string {
  try {
    return new URL(uri).host;
  } catch {
    return uri;
  }
}

const OAuthConsent = () => {
  const [params] = useSearchParams();
  const authorizationId = params.get("authorization_id") ?? "";
  const [details, setDetails] = useState<Details | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [selected, setSelected] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<"approve" | "deny" | null>(null);

  useEffect(() => {
    let active = true;
    (async () => {
      if (!authorizationId) {
        setError("This connection link is incomplete. Start the connection again from Claude.");
        return;
      }
      const { data: sess } = await supabase.auth.getSession();
      if (!sess.session) {
        const next = window.location.pathname + window.location.search;
        window.location.replace(`/auth?returnTo=${encodeURIComponent(next)}`);
        return;
      }
      const { data, error: detailsErr } = await supabase.auth.oauth.getAuthorizationDetails(authorizationId);
      if (!active) return;
      if (detailsErr) {
        setError("This connection request has expired or is no longer valid. Start the connection again from Claude.");
        return;
      }
      const immediate = (data as { redirect_url?: string } | null)?.redirect_url;
      if (immediate && !(data as Partial<Details>).client) {
        window.location.href = immediate;
        return;
      }
      setDetails(data as Details);
      const { data: ws } = await supabase.rpc("mcp_oauth_eligible_workspaces");
      if (!active) return;
      const list = (ws ?? []) as Workspace[];
      setWorkspaces(list);
      const firstEligible = list.find((w) => w.eligible);
      if (firstEligible) setSelected(firstEligible.workspace_id);
    })();
    return () => {
      active = false;
    };
  }, [authorizationId]);

  const decide = async (approve: boolean) => {
    if (!details) return;
    setBusy(approve ? "approve" : "deny");
    setError(null);
    if (approve) {
      const { error: grantErr } = await supabase.rpc("mcp_oauth_record_grant", {
        _client_id: details.client.id,
        _client_name: details.client.name,
        _workspace_id: selected,
      });
      if (grantErr) {
        setBusy(null);
        setError(
          grantErr.message.includes("plan_not_eligible")
            ? "That workspace needs the Plus plan or higher."
            : "We couldn't save this connection. Please try again.",
        );
        return;
      }
    }
    const res = approve
      ? await supabase.auth.oauth.approveAuthorization(authorizationId, { skipBrowserRedirect: true })
      : await supabase.auth.oauth.denyAuthorization(authorizationId, { skipBrowserRedirect: true });
    const target = (res.data as { redirect_url?: string } | null)?.redirect_url;
    if (res.error || !target) {
      setBusy(null);
      setError(approve ? "Approval failed. Please try again." : "Cancelling failed. Please close this window.");
      return;
    }
    window.location.href = target;
  };

  const switchAccount = async () => {
    await supabase.auth.signOut();
    const next = window.location.pathname + window.location.search;
    window.location.replace(`/auth?returnTo=${encodeURIComponent(next)}`);
  };

  const clientName = details?.client?.name || "Claude";
  const scopes = (details?.scope ?? "").split(/\s+/).filter(Boolean);
  const hasEligible = workspaces.some((w) => w.eligible);

  return (
    <div className="min-h-dvh flex items-center justify-center p-4 bg-background">
      <SeoHead title="Connect an app to Excerptus" description="Approve a connection to your Excerptus workspace." />
      <Card className="w-full max-w-lg shadow-elevated">
        <CardHeader className="space-y-2">
          <p className="font-serif text-lg font-semibold text-primary">Excerptus</p>
          <h1 className="font-serif text-2xl font-semibold">
            {details ? `Connect ${clientName} to Excerptus` : error ? "Connection problem" : "Loading…"}
          </h1>
        </CardHeader>
        <CardContent className="space-y-5">
          {!details && !error && (
            <div className="flex items-center gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Checking this connection request…
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Something needs attention</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {details && (
            <>
              <p className="text-sm text-muted-foreground">
                {clientName} ({hostOf(details.redirect_uri)}) will be able to use the Excerptus tools as you, inside the
                workspace you choose below.
              </p>

              <div className="rounded-md border border-border p-3 text-sm flex items-center justify-between gap-2">
                <span>
                  Signed in as <span className="font-medium">{details.user?.email}</span>
                </span>
                <Button variant="link" size="sm" className="h-auto p-0" onClick={switchAccount}>
                  Switch account
                </Button>
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">Workspace</p>
                {workspaces.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No workspaces found for this account.</p>
                ) : (
                  <RadioGroup value={selected} onValueChange={setSelected} className="space-y-1">
                    {workspaces.map((w) => (
                      <div key={w.workspace_id} className="flex items-center gap-2">
                        <RadioGroupItem value={w.workspace_id} id={w.workspace_id} disabled={!w.eligible} />
                        <Label htmlFor={w.workspace_id} className={w.eligible ? "" : "text-muted-foreground"}>
                          {w.label}
                          {!w.eligible && " — needs Plus or higher"}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                )}
                {!hasEligible && workspaces.length > 0 && (
                  <p className="text-sm text-muted-foreground">
                    Connecting Claude needs the Plus plan or higher.{" "}
                    <Link to="/pricing" className="text-primary underline">See plans</Link>
                  </p>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm font-medium">{clientName} will be able to</p>
                <ul className="space-y-1 text-sm">
                  {CAPABILITIES.map((c) => (
                    <li key={c} className="flex gap-2"><Check className="h-4 w-4 mt-0.5 text-primary shrink-0" />{c}</li>
                  ))}
                  {scopes.map((s) => (
                    <li key={s} className="flex gap-2 text-muted-foreground">
                      <Check className="h-4 w-4 mt-0.5 shrink-0" />
                      {SCOPE_LABELS[s] ?? `Additional permission requested: ${s}`}
                    </li>
                  ))}
                </ul>
                <p className="text-xs text-muted-foreground">
                  This doesn't bypass your workspace permissions. You can disconnect anytime in Settings → Integrations.
                </p>
              </div>

              <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">
                <Button variant="outline" disabled={!!busy} onClick={() => decide(false)}>
                  {busy === "deny" && <Loader2 className="h-4 w-4 animate-spin" />} Cancel connection
                </Button>
                <Button disabled={!!busy || !selected || !hasEligible} onClick={() => decide(true)}>
                  {busy === "approve" && <Loader2 className="h-4 w-4 animate-spin" />} Approve
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OAuthConsent;
